import { CONTEXT_PATH } from '@confluence/named-routes/entry-points/context.ts';

import type { PageTreeNode } from './pageTreeTypes.ts';
import type { PageTreeMacroChildrenQuery$data } from './queries/__generated__/PageTreeMacroChildrenQuery.graphql.ts';
import type { PageTreeMacroSpaceHomepageQuery$data } from './queries/__generated__/PageTreeMacroSpaceHomepageQuery.graphql.ts';
import type { PageTreeMacroAncestorsQuery$data } from './queries/__generated__/PageTreeMacroAncestorsQuery.graphql.ts';
import type { PageTreeMacroSearchByTitleQuery$data } from './queries/__generated__/PageTreeMacroSearchByTitleQuery.graphql.ts';
import type { PageTreeMacroExcerptQuery$data } from './queries/__generated__/PageTreeMacroExcerptQuery.graphql.ts';
import type { PageTreeMacroExcerptBatchQuery$data } from './queries/__generated__/PageTreeMacroExcerptBatchQuery.graphql.ts';

// ---------------------------------------------------------------------------
// Raw ptpage node shape (from GraphQL response)
// ---------------------------------------------------------------------------

export interface PtPageRawNode {
	id: string;
	title: string;
	hasChildren: boolean;
	status?: string;
	type?: string;
	subType?: string | null;
	createdDate?: { value?: string };
	lastUpdatedDate?: { value?: string };
	links?: { webui?: string };
}

// ---------------------------------------------------------------------------
// Node mapping
// ---------------------------------------------------------------------------

export const mapPtPageNode = (node: PtPageRawNode): PageTreeNode => ({
	id: node.id,
	title: node.title,
	hasChildren: node.hasChildren,
	url: node.links?.webui ? `${CONTEXT_PATH}${node.links.webui}` : '',
	status: node.status,
	type: node.type,
	subType: node.subType,
	createdDate: node.createdDate?.value ? Number(node.createdDate.value) : undefined,
	lastModifiedDate: node.lastUpdatedDate?.value ? Number(node.lastUpdatedDate.value) : undefined,
});

// ---------------------------------------------------------------------------
// Client-side sorting
// ---------------------------------------------------------------------------

const naturalCompare = (a: string, b: string): number => {
	return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
};

export const sortNodes = (
	nodes: PageTreeNode[],
	sort: string,
	reverse: boolean,
): PageTreeNode[] => {
	const sorted = [...nodes];

	switch (sort) {
		case 'bitwise':
			sorted.sort((a, b) => a.title.localeCompare(b.title));
			break;
		case 'natural':
			sorted.sort((a, b) => naturalCompare(a.title, b.title));
			break;
		case 'creation':
			sorted.sort((a, b) => (a.createdDate || 0) - (b.createdDate || 0));
			break;
		case 'modified':
			sorted.sort((a, b) => (a.lastModifiedDate || 0) - (b.lastModifiedDate || 0));
			break;
		case 'position':
		default:
			break;
	}

	if (reverse) {
		sorted.reverse();
	}

	return sorted;
};

// ---------------------------------------------------------------------------
// Children extraction from ptpage response
// ---------------------------------------------------------------------------

export const CHILDREN_PAGE_SIZE = 100;

type NullablePtPageRawNodes = ReadonlyArray<PtPageRawNode | null | undefined>;

/**
 * Extracts child nodes from a PageTreeMacroChildrenQuery response,
 * filters nulls, maps to PageTreeNode, and sorts.
 */
export const extractChildNodes = (
	data: PageTreeMacroChildrenQuery$data | undefined,
	sort: string,
	reverse: boolean,
): PageTreeNode[] | null => {
	const children = data?.ptpage?.children?.nodes;
	if (!children) {
		return null;
	}
	const nodes: PageTreeNode[] = [];
	for (const n of children as NullablePtPageRawNodes) {
		if (n != null) {
			nodes.push(mapPtPageNode(n));
		}
	}
	return sortNodes(nodes, sort, reverse);
};

export const extractChildren = async (
	refetchChildren: (variables: any) => Promise<any>,
	parentId: string,
	sort: string,
	reverse: boolean,
): Promise<PageTreeNode[]> => {
	const batches: NullablePtPageRawNodes[] = [];
	let after: string | null = null;
	let hasMore = true;
	let iteration = 0;

	while (hasMore && iteration < 100) {
		const result = await refetchChildren({
			contentId: parentId,
			first: CHILDREN_PAGE_SIZE,
			after,
		});

		const children = result?.data?.ptpage?.children;
		if (!children?.nodes) {
			break;
		}

		batches.push(children.nodes as NullablePtPageRawNodes);

		if (children.pageInfo?.hasNextPage && children.pageInfo?.endCursor !== after) {
			after = children.pageInfo.endCursor ?? null;
		} else {
			hasMore = false;
		}

		iteration++;
	}

	const rawNodes = batches.flat().filter((n): n is PtPageRawNode => n != null);
	return sortNodes(rawNodes.map(mapPtPageNode), sort, reverse);
};

// ---------------------------------------------------------------------------
// CQL sanitization
// ---------------------------------------------------------------------------

export const sanitizeCqlValue = (value: string): string => {
	return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
};

// ---------------------------------------------------------------------------
// Root parameter parsing (shared by preloader and component)
// ---------------------------------------------------------------------------

export type RootType = 'self' | 'parent' | 'home' | 'none' | 'title';

// ---------------------------------------------------------------------------
// Macro parameter parsing
// ---------------------------------------------------------------------------

export type SortField = 'natural' | 'bitwise' | 'position' | 'creation' | 'modified';

const isValidSort = (value: string): value is SortField =>
	['natural', 'bitwise', 'position', 'creation', 'modified'].includes(value);

export interface ParsedPageTreeMacroParams {
	rootParam: string | null;
	spaceKey: string;
	showExcerpt: boolean;
	sort: SortField;
	reverse: boolean;
	showSearchBox: boolean;
	showExpandCollapseAll: boolean;
	startDepth: number;
}

/**
 * Parses raw macro parameters into typed, validated values.
 * Used by the renderer component and available for the preloader.
 */
export const parseMacroParams = (
	macroParams: Record<string, { value?: string }> | undefined,
	defaultSpaceKey: string,
): ParsedPageTreeMacroParams => {
	const rootParam = macroParams?.root?.value ?? null;
	const spaceKey = macroParams?.spaces?.value ?? defaultSpaceKey;
	const showExcerpt = macroParams?.excerpt?.value === 'true';
	const sortValue = macroParams?.sort?.value ?? 'position';
	const sort: SortField = isValidSort(sortValue) ? sortValue : 'position';
	const reverse = macroParams?.reverse?.value === 'true';
	const showSearchBox = macroParams?.searchBox?.value === 'true';
	const showExpandCollapseAll = macroParams?.expandCollapseAll?.value === 'true';
	const startDepthStr = macroParams?.startDepth?.value ?? '0';
	const startDepth = /^\d+$/.test(startDepthStr) ? parseInt(startDepthStr, 10) : 0;

	return {
		rootParam,
		spaceKey,
		showExcerpt,
		sort,
		reverse,
		showSearchBox,
		showExpandCollapseAll,
		startDepth,
	};
};

/**
 * Serializes parsed macro params to a JSON string for the data-macro-parameters attribute.
 * This is used by the Confluence editor to reconstruct macro params when editing.
 */
export const serializeMacroParams = (params: ParsedPageTreeMacroParams): string =>
	JSON.stringify({
		root: params.rootParam,
		spaces: params.spaceKey,
		excerpt: String(params.showExcerpt),
		sort: params.sort,
		reverse: String(params.reverse),
		searchBox: String(params.showSearchBox),
		expandCollapseAll: String(params.showExpandCollapseAll),
		startDepth: String(params.startDepth),
	});

/**
 * Parses the macro's root parameter to determine the root type.
 * Used by both the preload handler and usePageTreeData to ensure
 * the same logic is applied in both places.
 */
export const parseRootType = (rootParam: string | null | undefined): RootType => {
	if (!rootParam) {
		return 'home';
	}
	const lower = rootParam.toLowerCase();
	switch (lower) {
		case '@self':
			return 'self';
		case '@parent':
			return 'parent';
		case '@home':
			return 'home';
		case '@none':
			return 'none';
		default:
			return 'title';
	}
};

// ---------------------------------------------------------------------------
// Excerpt extraction (individual query — used for dynamically expanded nodes)
// ---------------------------------------------------------------------------

/** Extracts the raw ADF string from a single-node ExcerptQuery response (used for dynamically expanded nodes). */
export const extractSingleNodeAdfString = (
	data: PageTreeMacroExcerptQuery$data | null | undefined,
): string | undefined => data?.content?.nodes?.[0]?.macroExcerpt?.atlasDocFormat ?? undefined;

/** Extracts the media token from a single-node ExcerptQuery response (used for dynamically expanded nodes). */
export const extractSingleNodeMediaToken = (
	data: PageTreeMacroExcerptQuery$data | null | undefined,
): string | undefined =>
	data?.content?.nodes?.[0]?.body?.atlas_doc_format?.mediaToken?.token ?? undefined;

// ---------------------------------------------------------------------------
// Excerpt map (batch query support)
// ---------------------------------------------------------------------------

export interface ExcerptData {
	adfString?: string;
	mediaToken?: string;
}

/** Keyed by content ID → excerpt data for that node. */
export type ExcerptMap = ReadonlyMap<string, ExcerptData>;

export const buildExcerptMap = (
	data: PageTreeMacroExcerptBatchQuery$data | null | undefined,
	requestedIds?: readonly string[],
): ExcerptMap => {
	const map = new Map<string, ExcerptData>();
	// Mark all requested IDs as fetched (empty excerpt) so the component
	// won't fire individual queries for IDs the API didn't return (e.g. whiteboards)
	if (requestedIds) {
		for (const id of requestedIds) {
			map.set(id, {});
		}
	}
	const nodes = data?.content?.nodes;
	if (nodes) {
		for (const node of nodes) {
			if (!node?.id) {
				continue;
			}
			const adfString = node.macroExcerpt?.atlasDocFormat ?? undefined;
			const mediaToken = node.body?.atlas_doc_format?.mediaToken?.token ?? undefined;
			map.set(node.id, { adfString, mediaToken });
		}
	}
	return map;
};

/**
 * Extracts child node IDs from a PageTreeMacroChildrenQuery response.
 * Used by the preload handler to collect visible node IDs for excerpt preloading.
 */
export const extractChildNodeIds = (
	data: PageTreeMacroChildrenQuery$data | null | undefined,
): string[] => {
	const children = data?.ptpage?.children?.nodes;
	if (!children) {
		return [];
	}
	const ids: string[] = [];
	for (const child of children) {
		if (child?.id) {
			ids.push(child.id);
		}
	}
	return ids;
};

/** Extracts the homepage ID from a SpaceHomepageQuery response. */
export const extractHomepageId = (
	data: PageTreeMacroSpaceHomepageQuery$data | null | undefined,
): string | undefined => data?.space?.homepage?.id ?? undefined;

/** Extracts the nearest ancestor (parent) ID from an AncestorsQuery response. */
export const extractParentAncestorId = (
	data: PageTreeMacroAncestorsQuery$data | null | undefined,
): string | undefined => data?.ptpage?.nearestAncestors?.nodes?.[0]?.id ?? undefined;

/** Extracts the page ID from a SearchByTitleQuery response. */
export const extractSearchResultId = (
	data: PageTreeMacroSearchByTitleQuery$data | null | undefined,
): string | undefined => data?.confluence_search?.nodes?.[0]?.content?.id ?? undefined;

/**
 * Extracts the rootId from query results based on root type.
 * Used by both the preload handler and usePageTreeData to derive the
 * root page ID from whichever resolution query ran.
 */
export const resolveRootId = ({
	rootType,
	contentId,
	homepageData,
	ancestorData,
	searchData,
}: {
	rootType: RootType;
	contentId: string;
	homepageData?: PageTreeMacroSpaceHomepageQuery$data | null;
	ancestorData?: PageTreeMacroAncestorsQuery$data | null;
	searchData?: PageTreeMacroSearchByTitleQuery$data | null;
}): string | undefined => {
	switch (rootType) {
		case 'self':
			return contentId;
		case 'home':
			return extractHomepageId(homepageData);
		case 'parent':
			return extractParentAncestorId(ancestorData);
		case 'title':
			return extractSearchResultId(searchData);
		case 'none':
			return undefined;
	}
};

/**
 * Whether this root type needs ancestor preloading for auto-expansion.
 * - @self: current page is the root, no ancestors to expand
 * - @parent: current page is a direct child of root, no intermediate ancestors
 */
export const needsAncestorExpansion = (rootType: RootType): boolean =>
	rootType !== 'self' && rootType !== 'parent';

/**
 * Whether this root type needs a children query for the root node.
 * - @none: shows top-level pages via RootLevelQuery, not children
 */
export const needsRootChildren = (rootType: RootType): boolean => rootType !== 'none';

/**
 * Extracts the page title from a root parameter, stripping
 * the optional "spaceKey:" prefix (e.g. "DS:My Page" → "My Page").
 */
export const parsePageTitle = (rootParam: string): string => {
	const colonIndex = rootParam.indexOf(':');
	return colonIndex >= 0 ? rootParam.substring(colonIndex + 1) : rootParam;
};

/**
 * Builds a CQL query string for searching a page by title within a space.
 */
export const buildTitleSearchCql = (pageTitle: string, spaceKey: string): string => {
	return `title = "${sanitizeCqlValue(pageTitle)}" and space = "${sanitizeCqlValue(spaceKey)}" and type = "page"`;
};
